import dayjs from 'dayjs'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Typogaphy } from 'vnetwork-ui'
import * as Popover from '@radix-ui/react-popover'
import {
    Calendar,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    Clock,
} from 'lucide-react'

export interface MonthRange {
    gte: string // YYYY-MM-DD format (first day of month)
    lte: string // YYYY-MM-DD format (last day of month)
}

interface MonthRangeSelectorProps {
    value?: MonthRange
    onChange: (range: MonthRange) => void
    className?: string
    label?: string
    placement?: 'left' | 'right'
    options?: PresetOption[]
}

type MonthRangeOption =
    | 'this_month'
    | 'last_month'
    | 'last_3_months'
    | 'last_6_months'
    | 'this_quarter'
    | 'last_quarter'
    | 'q1'
    | 'q2'
    | 'q3'
    | 'q4'
    | 'this_year'
    | 'last_year'
    | 'last_2_years'
    | 'custom'

interface PresetOption {
    value: MonthRangeOption
    label: string
    icon?: React.ReactNode
    category?: 'month' | 'quarter' | 'year'
}

/**
 * MonthRangeSelector component
 * @param value - The current month range value
 * @param onChange - The function to call when the month range changes
 * @param className - The class name to apply to the component
 * @param label - The label to display for the component
 * @param placement - The placement of the component
 * @param options - The options to display in the component
 */
const MonthRangeSelector: React.FC<MonthRangeSelectorProps> = ({
    value,
    onChange,
    className = '',
    label,
    placement = 'top',
    options = [],
}) => {
    const { t } = useTranslation()
    const [selectedOption, setSelectedOption] =
        useState<MonthRangeOption>('last_month')
    const [isOpen, setIsOpen] = useState(false)
    const [customStartMonth, setCustomStartMonth] = useState<string>('')
    const [customEndMonth, setCustomEndMonth] = useState<string>('')
    const [viewMode, setViewMode] = useState<'preset' | 'custom'>('preset')
    const [calendarYear, setCalendarYear] = useState(dayjs().year())
    const [selectingStart, setSelectingStart] = useState(true)

    const presetOptions: PresetOption[] = [
        {
            value: 'this_month',
            label: 'date_range.this_month',
            category: 'month',
        },
        {
            value: 'last_month',
            label: 'date_range.last_month',
            category: 'month',
        },
        {
            value: 'last_3_months',
            label: 'date_range.last_3_months',
            category: 'month',
        },
        {
            value: 'last_6_months',
            label: 'date_range.last_6_months',
            category: 'month',
        },
        {
            value: 'this_quarter',
            label: 'date_range.this_quarter',
            category: 'quarter',
        },
        {
            value: 'last_quarter',
            label: 'date_range.last_quarter',
            category: 'quarter',
        },
        { value: 'q1', label: 'date_range.q1', category: 'quarter' },
        { value: 'q2', label: 'date_range.q2', category: 'quarter' },
        { value: 'q3', label: 'date_range.q3', category: 'quarter' },
        { value: 'q4', label: 'date_range.q4', category: 'quarter' },
        {
            value: 'this_year',
            label: 'date_range.this_year',
            category: 'year',
        },
        {
            value: 'last_year',
            label: 'date_range.last_year',
            category: 'year',
        },
        {
            value: 'last_2_years',
            label: 'date_range.last_2_years',
            category: 'year',
        },
    ]
    const allOptions = [...presetOptions, ...options]

    const groupedPresets = useMemo(() => {
        return {
            month: allOptions.filter((o) => o.category === 'month'),
            quarter: allOptions.filter((o) => o.category === 'quarter'),
            year: allOptions.filter((o) => o.category === 'year'),
        }
    }, [])

    const getSelectedLabel = () => {
        if (selectedOption === 'custom') {
            if (value?.gte && value?.lte) {
                return `${dayjs(value.gte).format('MMM YYYY')} — ${dayjs(value.lte).format('MMM YYYY')}`
            }
            return t('date_range.custom')
        }
        return t(
            presetOptions.find((opt) => opt.value === selectedOption)?.label ||
                'date_range.last_month'
        )
    }

    const calculateMonthRange = (option: MonthRangeOption): MonthRange => {
        const now = dayjs()

        switch (option) {
            case 'this_month':
                return {
                    gte: now.startOf('month').format('YYYY-MM-DD'),
                    lte: now.endOf('month').format('YYYY-MM-DD'),
                }

            case 'last_month': {
                const lastMonth = now.subtract(1, 'month')
                return {
                    gte: lastMonth.startOf('month').format('YYYY-MM-DD'),
                    lte: lastMonth.endOf('month').format('YYYY-MM-DD'),
                }
            }

            case 'last_3_months': {
                const threeMonthsAgo = now.subtract(2, 'month')
                return {
                    gte: threeMonthsAgo.startOf('month').format('YYYY-MM-DD'),
                    lte: now.endOf('month').format('YYYY-MM-DD'),
                }
            }

            case 'last_6_months': {
                const sixMonthsAgo = now.subtract(5, 'month')
                return {
                    gte: sixMonthsAgo.startOf('month').format('YYYY-MM-DD'),
                    lte: now.endOf('month').format('YYYY-MM-DD'),
                }
            }

            case 'this_quarter': {
                const currentQuarter = Math.floor(now.month() / 3)
                const quarterStart = now
                    .month(currentQuarter * 3)
                    .startOf('month')
                const quarterEnd = now
                    .month(currentQuarter * 3 + 2)
                    .endOf('month')
                return {
                    gte: quarterStart.format('YYYY-MM-DD'),
                    lte: quarterEnd.format('YYYY-MM-DD'),
                }
            }

            case 'last_quarter': {
                const currentQuarter = Math.floor(now.month() / 3)
                const lastQuarter =
                    currentQuarter === 0 ? 3 : currentQuarter - 1
                const year = currentQuarter === 0 ? now.year() - 1 : now.year()
                const quarterStart = dayjs()
                    .year(year)
                    .month(lastQuarter * 3)
                    .startOf('month')
                const quarterEnd = dayjs()
                    .year(year)
                    .month(lastQuarter * 3 + 2)
                    .endOf('month')
                return {
                    gte: quarterStart.format('YYYY-MM-DD'),
                    lte: quarterEnd.format('YYYY-MM-DD'),
                }
            }

            case 'q1':
                return {
                    gte: now.month(0).startOf('month').format('YYYY-MM-DD'),
                    lte: now.month(2).endOf('month').format('YYYY-MM-DD'),
                }

            case 'q2':
                return {
                    gte: now.month(3).startOf('month').format('YYYY-MM-DD'),
                    lte: now.month(5).endOf('month').format('YYYY-MM-DD'),
                }

            case 'q3':
                return {
                    gte: now.month(6).startOf('month').format('YYYY-MM-DD'),
                    lte: now.month(8).endOf('month').format('YYYY-MM-DD'),
                }

            case 'q4':
                return {
                    gte: now.month(9).startOf('month').format('YYYY-MM-DD'),
                    lte: now.month(11).endOf('month').format('YYYY-MM-DD'),
                }

            case 'this_year':
                return {
                    gte: now.startOf('year').format('YYYY-MM-DD'),
                    lte: now.endOf('year').format('YYYY-MM-DD'),
                }

            case 'last_year': {
                const lastYear = now.subtract(1, 'year')
                return {
                    gte: lastYear.startOf('year').format('YYYY-MM-DD'),
                    lte: lastYear.endOf('year').format('YYYY-MM-DD'),
                }
            }

            case 'last_2_years': {
                const lastTwoYears = now.subtract(1, 'year')
                return {
                    gte: lastTwoYears.startOf('year').format('YYYY-MM-DD'),
                    lte: now.endOf('year').format('YYYY-MM-DD'),
                }
            }

            case 'custom':
                if (customStartMonth && customEndMonth) {
                    return {
                        gte: dayjs(customStartMonth)
                            .startOf('month')
                            .format('YYYY-MM-DD'),
                        lte: dayjs(customEndMonth)
                            .endOf('month')
                            .format('YYYY-MM-DD'),
                    }
                } else {
                    const defaultLastMonth = now.subtract(1, 'month')
                    return {
                        gte: defaultLastMonth
                            .startOf('month')
                            .format('YYYY-MM-DD'),
                        lte: defaultLastMonth
                            .endOf('month')
                            .format('YYYY-MM-DD'),
                    }
                }

            default: {
                const defaultLastMonth = now.subtract(1, 'month')
                return {
                    gte: defaultLastMonth.startOf('month').format('YYYY-MM-DD'),
                    lte: defaultLastMonth.endOf('month').format('YYYY-MM-DD'),
                }
            }
        }
    }

    const handleOptionChange = (option: MonthRangeOption) => {
        setSelectedOption(option)
        if (option !== 'custom') {
            setIsOpen(false)
            const range = calculateMonthRange(option)
            onChange(range)
        }
    }

    const handleMonthClick = (year: number, month: number) => {
        const monthStr = dayjs().year(year).month(month).format('YYYY-MM')

        if (selectingStart) {
            setCustomStartMonth(monthStr)
            setSelectingStart(false)
        } else {
            setCustomEndMonth(monthStr)
            setSelectingStart(true)

            // Auto apply when both selected
            const range: MonthRange = {
                gte: dayjs(customStartMonth)
                    .startOf('month')
                    .format('YYYY-MM-DD'),
                lte: dayjs(monthStr).endOf('month').format('YYYY-MM-DD'),
            }
            onChange(range)
            setIsOpen(false)
        }
    }

    const isMonthInRange = (year: number, month: number): boolean => {
        if (!customStartMonth || !customEndMonth) return false

        const monthDate = dayjs().year(year).month(month)
        const start = dayjs(customStartMonth)
        const end = dayjs(customEndMonth)

        return (
            monthDate.isSame(start, 'month') ||
            monthDate.isSame(end, 'month') ||
            (monthDate.isAfter(start, 'month') &&
                monthDate.isBefore(end, 'month'))
        )
    }

    const isMonthSelected = (year: number, month: number): boolean => {
        const monthStr = dayjs().year(year).month(month).format('YYYY-MM')
        return monthStr === customStartMonth || monthStr === customEndMonth
    }

    const detectMonthRangeOption = (range: MonthRange): MonthRangeOption => {
        if (!range.gte || !range.lte) return 'last_month'

        const startDate = dayjs(range.gte)
        const endDate = dayjs(range.lte)

        // Check all preset options
        for (const option of presetOptions) {
            const optionRange = calculateMonthRange(option.value)
            if (
                startDate.format('YYYY-MM-DD') === optionRange.gte &&
                endDate.format('YYYY-MM-DD') === optionRange.lte
            ) {
                return option.value
            }
        }

        return 'custom'
    }

    useEffect(() => {
        if (!value) {
            const initialRange = calculateMonthRange('last_month')
            onChange(initialRange)
        } else {
            const detectedOption = detectMonthRangeOption(value)
            setSelectedOption(detectedOption)

            if (detectedOption === 'custom') {
                setCustomStartMonth(dayjs(value.gte).format('YYYY-MM'))
                setCustomEndMonth(dayjs(value.lte).format('YYYY-MM'))
                setViewMode('custom')
            }
        }
    }, [value?.gte, value?.lte])

    const renderMonthGrid = () => {
        const months = Array.from({ length: 12 }, (_, i) => i)
        const monthNames = [
            'Jan',
            'Feb',
            'Mar',
            'Apr',
            'May',
            'Jun',
            'Jul',
            'Aug',
            'Sep',
            'Oct',
            'Nov',
            'Dec',
        ]

        return (
            <div className="p-0">
                {/* Year Selector */}
                <div className="flex items-center justify-between mb-2">
                    <button
                        onClick={() => setCalendarYear(calendarYear - 1)}
                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                    >
                        <ChevronLeft className="w-5 h-5 text-gray-600" />
                    </button>
                    <Typogaphy className="text-gray-900">
                        {calendarYear}
                    </Typogaphy>
                    <button
                        onClick={() => setCalendarYear(calendarYear + 1)}
                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                    >
                        <ChevronRight className="w-5 h-5 text-gray-600" />
                    </button>
                </div>

                {/* Range Info */}
                <div className="mb-2 text-center capitalize">
                    <Typogaphy className="text-xs text-gray-600">
                        {selectingStart ? (
                            <span>
                                {t('selectors.select')}{' '}
                                <strong>{t('selectors.start_month')}</strong>
                            </span>
                        ) : (
                            <span>
                                {t('selectors.select')}{' '}
                                <strong>{t('selectors.end_month')}</strong>
                            </span>
                        )}
                    </Typogaphy>
                    {customStartMonth && (
                        <Typogaphy className="text-xs text-primary mt-1">
                            {dayjs(customStartMonth).format('MMM YYYY')}
                            {customEndMonth &&
                                ` → ${dayjs(customEndMonth).format('MMM YYYY')}`}
                        </Typogaphy>
                    )}
                </div>

                {/* Month Grid */}
                <div className="grid grid-cols-3 gap-2">
                    {months?.map((month) => {
                        const isSelected = isMonthSelected(calendarYear, month)
                        const isInRange = isMonthInRange(calendarYear, month)
                        const isCurrentMonth =
                            dayjs().year() === calendarYear &&
                            dayjs().month() === month

                        return (
                            <button
                                key={month}
                                onClick={() =>
                                    handleMonthClick(calendarYear, month)
                                }
                                className={`
                                    px-4 py-2 text-sm font-medium rounded-md transition-all
                                    ${
                                        isSelected
                                            ? 'bg-primary text-white shadow-md scale-105'
                                            : isInRange
                                              ? 'bg-primary-lighter text-primary'
                                              : isCurrentMonth
                                                ? 'border-2 border-primary text-primary'
                                                : 'hover:bg-gray-100 text-gray-700'
                                    }
                                `}
                            >
                                {monthNames[month]}
                            </button>
                        )
                    })}
                </div>

                {/* Quick Reset */}
                <div className="mt-2 pt-2 border-t border-gray-200">
                    <button
                        onClick={() => {
                            setCustomStartMonth('')
                            setCustomEndMonth('')
                            setSelectingStart(true)
                        }}
                        className="w-full text-sm text-gray-600 hover:text-primary transition-colors"
                    >
                        {t('selectors.reset_selection')}
                    </button>
                </div>
            </div>
        )
    }

    return (
        <div
            className={`${className} relative month-range-selector xs:w-full md:w-auto xs:min-w-full md:min-w-auto`}
            data-ui-component="MonthRangeSelector"
        >
            {label && (
                <label className="block text-sm text-gray-700 mb-1.5">
                    {t(label)}
                </label>
            )}

            <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
                <Popover.Trigger asChild>
                    <button
                        className={`
                            relative w-full bg-white border rounded-[4px] px-4 py-2
                            text-sm text-left text-gray-700
                            hover:border-primary hover:shadow-sm
                            focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20
                            transition-all duration-200 flex items-center justify-between gap-2
                            ${isOpen ? 'border-primary ring-2 ring-primary/20 shadow-md' : 'border-gray-300'}
                        `}
                    >
                        <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-gray-500" />
                            <span className="">{getSelectedLabel()}</span>
                        </div>
                        <ChevronDown
                            className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${
                                isOpen ? 'rotate-180' : ''
                            }`}
                        />
                    </button>
                </Popover.Trigger>

                <Popover.Portal>
                    <Popover.Content
                        align={placement === 'left' ? 'start' : 'end'}
                        sideOffset={5}
                        className={`
                            xs:min-w-[90vw] md:min-w-[400px]
                            bg-white border border-gray-200 rounded-md shadow-xl z-50
                            animate-in fade-in zoom-in-95 duration-200
                            overflow-hidden
                        `}
                    >
                        {/* Tab Headers */}
                        <div className="flex border-b border-gray-200 bg-gray-50">
                            <button
                                onClick={() => setViewMode('preset')}
                                className={`
                                flex-1 px-4 py-2.5 text-sm font-medium transition-colors
                                flex items-center justify-center gap-2
                                ${
                                    viewMode === 'preset'
                                        ? 'text-primary border-b-2 border-primary bg-white'
                                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                                }
                            `}
                            >
                                <Clock className="w-4 h-4" />
                                {t('selectors.quick_select')}
                            </button>
                            <button
                                onClick={() => {
                                    setViewMode('custom')
                                    setSelectedOption('custom')
                                }}
                                className={`
                                flex-1 px-4 py-2.5 text-sm font-medium transition-colors
                                flex items-center justify-center gap-2
                                ${
                                    viewMode === 'custom'
                                        ? 'text-primary border-b-2 border-primary bg-white'
                                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                                }
                            `}
                            >
                                <Calendar className="w-4 h-4" />
                                {t('selectors.custom')}
                            </button>
                        </div>

                        {/* Content */}
                        {viewMode === 'preset' ? (
                            <div className="p-2 min-w-[280px] max-h-[400px] overflow-y-auto">
                                {/* Month Presets */}
                                <div className="mb-3">
                                    <Typogaphy className="px-2 py-1 text-xs font-semibold text-gray-500 uppercase">
                                        {t('selectors.months')}
                                    </Typogaphy>
                                    <div className="space-y-0.5">
                                        {groupedPresets.month.map((option) => (
                                            <button
                                                key={option.value}
                                                className={`
                                                w-full px-3 py-2 text-left text-sm rounded-md
                                                transition-all duration-150
                                                ${
                                                    selectedOption ===
                                                    option.value
                                                        ? 'bg-primary text-white font-medium shadow-sm'
                                                        : 'text-gray-700 hover:bg-gray-100'
                                                }
                                            `}
                                                onClick={() =>
                                                    handleOptionChange(
                                                        option.value
                                                    )
                                                }
                                            >
                                                {t(option.label)}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Quarter Presets */}
                                <div className="mb-3">
                                    <Typogaphy className="px-2 py-1 text-xs font-semibold text-gray-500 uppercase">
                                        {t('selectors.quarters')}
                                    </Typogaphy>
                                    <div className="grid grid-cols-2 gap-1">
                                        {groupedPresets.quarter.map(
                                            (option) => (
                                                <button
                                                    key={option.value}
                                                    className={`
                                                px-3 py-2 text-sm rounded-md
                                                transition-all duration-150
                                                ${
                                                    selectedOption ===
                                                    option.value
                                                        ? 'bg-primary text-white font-medium shadow-sm'
                                                        : 'text-gray-700 hover:bg-gray-100'
                                                }
                                            `}
                                                    onClick={() =>
                                                        handleOptionChange(
                                                            option.value
                                                        )
                                                    }
                                                >
                                                    {t(option.label)}
                                                </button>
                                            )
                                        )}
                                    </div>
                                </div>

                                {/* Year Presets */}
                                <div>
                                    <Typogaphy className="px-2 py-1 text-xs font-semibold text-gray-500 uppercase">
                                        {t('selectors.years')}
                                    </Typogaphy>
                                    <div className="space-y-0.5">
                                        {groupedPresets.year.map((option) => (
                                            <button
                                                key={option.value}
                                                className={`
                                                w-full px-3 py-2 text-left text-sm rounded-md
                                                transition-all duration-150
                                                ${
                                                    selectedOption ===
                                                    option.value
                                                        ? 'bg-primary text-white font-medium shadow-sm'
                                                        : 'text-gray-700 hover:bg-gray-100'
                                                }
                                            `}
                                                onClick={() =>
                                                    handleOptionChange(
                                                        option.value
                                                    )
                                                }
                                            >
                                                {t(option.label)}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="min-w-[320px]">
                                {renderMonthGrid()}
                            </div>
                        )}
                    </Popover.Content>
                </Popover.Portal>
            </Popover.Root>
        </div>
    )
}

export default MonthRangeSelector
